import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { money } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { Plus, Package, Trash, X, Hash, PencilSimple, ClockCounterClockwise } from "@phosphor-icons/react";
import { toast } from "sonner";

const FIELD_LABELS = {
  asset_number: "Asset Number", name: "Name", category: "Category",
  location: "Location", value: "Fair Value", description: "Description",
};
function fieldLabel(f) {
  if (f?.startsWith("spec:")) return `Spec · ${f.slice(5)}`;
  return FIELD_LABELS[f] || f;
}
function fmtVal(v) {
  if (v === true) return "Yes";
  if (v === false) return "No";
  if (v === null || v === undefined || v === "") return "—";
  return String(v);
}

const EMPTY = { asset_number: "", name: "", category: "", location: "", value: 0, description: "", specifications: [] };

export default function Assets() {
  const [assets, setAssets] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editingId, setEditingId] = useState(null);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [history, setHistory] = useState([]);
  const [historyName, setHistoryName] = useState("");

  const load = () => api.get("/assets").then((r) => setAssets(r.data));
  useEffect(() => { load(); }, []);

  const openHistory = async (a) => {
    setHistoryName(a.name);
    setHistoryOpen(true);
    try { const { data } = await api.get(`/assets/${a.id}/history`); setHistory(data); }
    catch { setHistory([]); }
  };

  const openCreate = () => { setEditingId(null); setForm(EMPTY); setOpen(true); };
  const openEdit = (a) => {
    setEditingId(a.id);
    setForm({
      asset_number: a.asset_number, name: a.name, category: a.category || "",
      location: a.location || "", value: a.value || 0, description: a.description || "",
      specifications: a.specifications || [],
    });
    setOpen(true);
  };

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const addSpec = () => set("specifications", [...form.specifications, { key: "", value: "" }]);
  const updSpec = (i, k, v) => {
    const s = [...form.specifications]; s[i] = { ...s[i], [k]: v }; set("specifications", s);
  };
  const rmSpec = (i) => set("specifications", form.specifications.filter((_, x) => x !== i));

  const save = async () => {
    try {
      const payload = { ...form, value: parseFloat(form.value) || 0,
        specifications: form.specifications.filter((s) => s.key) };
      if (editingId) {
        await api.put(`/assets/${editingId}`, payload);
        toast.success("Asset updated");
      } else {
        await api.post("/assets", payload);
        toast.success("Asset created");
      }
      setOpen(false); setForm(EMPTY); setEditingId(null); load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const del = async (id) => {
    try { await api.delete(`/assets/${id}`); toast.success("Asset deleted"); load(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  return (
    <div className="space-y-8">
      <div className="flex items-end justify-between fade-up">
        <div>
          <p className="text-xs font-medium tracking-[0.2em] uppercase text-muted-foreground mb-2">Registry</p>
          <h1 className="font-display text-4xl sm:text-5xl font-bold tracking-tighter">Assets</h1>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <Button className="rounded-sm gap-2" data-testid="add-asset-btn" onClick={openCreate}><Plus size={16} /> New Asset</Button>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto bg-card">
            <DialogHeader><DialogTitle className="font-display tracking-tight">{editingId ? "Edit Asset" : "New Asset"}</DialogTitle></DialogHeader>
            <div className="grid grid-cols-2 gap-4 py-2">
              <div>
                <Label>Asset Number</Label>
                <Input value={form.asset_number} onChange={(e) => set("asset_number", e.target.value)} className="mt-1.5 rounded-sm font-mono" data-testid="asset-number" placeholder="AST-0001" />
              </div>
              <div>
                <Label>Name</Label>
                <Input value={form.name} onChange={(e) => set("name", e.target.value)} className="mt-1.5 rounded-sm" data-testid="asset-name" />
              </div>
              <div>
                <Label>Category</Label>
                <Input value={form.category} onChange={(e) => set("category", e.target.value)} className="mt-1.5 rounded-sm" data-testid="asset-category" placeholder="Property / Vehicle / Equipment" />
              </div>
              <div>
                <Label>Location</Label>
                <Input value={form.location} onChange={(e) => set("location", e.target.value)} className="mt-1.5 rounded-sm" data-testid="asset-location" />
              </div>
              <div>
                <Label>Fair Value</Label>
                <Input type="number" value={form.value} onChange={(e) => set("value", e.target.value)} className="mt-1.5 rounded-sm font-mono" data-testid="asset-value" />
              </div>
              <div className="col-span-2">
                <Label>Description</Label>
                <Textarea value={form.description} onChange={(e) => set("description", e.target.value)} className="mt-1.5 rounded-sm" data-testid="asset-description" />
              </div>
              <div className="col-span-2">
                <div className="flex items-center justify-between mb-2">
                  <Label>Specifications</Label>
                  <Button type="button" variant="outline" size="sm" onClick={addSpec} className="rounded-sm gap-1 h-8" data-testid="add-spec-btn"><Plus size={14} /> Add spec</Button>
                </div>
                <div className="space-y-2">
                  {form.specifications.map((s, i) => (
                    <div key={i} className="flex gap-2">
                      <Input placeholder="Key (e.g. Color)" value={s.key} onChange={(e) => updSpec(i, "key", e.target.value)} className="rounded-sm" data-testid={`spec-key-${i}`} />
                      <Input placeholder="Value (e.g. Red)" value={s.value} onChange={(e) => updSpec(i, "value", e.target.value)} className="rounded-sm" data-testid={`spec-value-${i}`} />
                      <Button type="button" variant="ghost" size="icon" onClick={() => rmSpec(i)} className="rounded-sm shrink-0"><X size={16} /></Button>
                    </div>
                  ))}
                  {form.specifications.length === 0 && <p className="text-xs text-muted-foreground">No specifications. Add flexible key/value pairs.</p>}
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button onClick={save} className="rounded-sm" data-testid="save-asset-btn">{editingId ? "Save Changes" : "Create Asset"}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {assets.length === 0 ? (
        <Card className="rounded-sm border-dashed border-border shadow-none p-16 text-center bg-card fade-up">
          <Package size={40} className="mx-auto text-muted-foreground mb-4" />
          <p className="text-muted-foreground text-sm">No assets yet. Register your first asset to link it to leases.</p>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {assets.map((a, i) => (
            <Card key={a.id} className="rounded-sm border-border shadow-none p-6 bg-card fade-up hover:-translate-y-1 transition-transform group"
              style={{ animationDelay: `${i * 40}ms` }} data-testid={`asset-card-${a.id}`}>
              <div className="flex items-start justify-between mb-3">
                <Badge variant="outline" className="rounded-sm font-mono gap-1"><Hash size={12} />{a.asset_number}</Badge>
                <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                  <Button variant="ghost" size="icon" onClick={() => openHistory(a)} data-testid={`history-asset-${a.id}`}
                    className="h-7 w-7 text-muted-foreground hover:text-primary rounded-sm"><ClockCounterClockwise size={15} /></Button>
                  <Button variant="ghost" size="icon" onClick={() => openEdit(a)} data-testid={`edit-asset-${a.id}`}
                    className="h-7 w-7 text-muted-foreground hover:text-primary rounded-sm"><PencilSimple size={15} /></Button>
                  <Button variant="ghost" size="icon" onClick={() => del(a.id)} data-testid={`delete-asset-${a.id}`}
                    className="h-7 w-7 text-muted-foreground hover:text-destructive rounded-sm"><Trash size={15} /></Button>
                </div>
              </div>
              <h3 className="font-display text-lg font-semibold tracking-tight">{a.name}</h3>
              <p className="text-xs text-muted-foreground mb-3">{a.category || "Uncategorized"}{a.location ? ` · ${a.location}` : ""}</p>
              {a.value > 0 && <p className="font-mono text-sm mb-3">Value: {money(a.value)}</p>}
              {a.specifications?.length > 0 && (
                <div className="border-t border-border pt-3 mt-3 space-y-1">
                  {a.specifications.map((s, x) => (
                    <div key={x} className="flex justify-between text-xs">
                      <span className="text-muted-foreground">{s.key}</span>
                      <span className="font-mono">{s.value}</span>
                    </div>
                  ))}
                </div>
              )}
            </Card>
          ))}
        </div>
      )}

      <Dialog open={historyOpen} onOpenChange={setHistoryOpen}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto bg-card">
          <DialogHeader><DialogTitle className="font-display tracking-tight">Change History — {historyName}</DialogTitle></DialogHeader>
          {history.length === 0 ? (
            <p className="text-sm text-muted-foreground py-4">No change history yet.</p>
          ) : (
            <div className="space-y-3 py-2" data-testid="asset-history-list">
              {history.map((h) => (
                <div key={h.id} className="border border-border rounded-sm p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <ClockCounterClockwise size={15} className="text-primary" />
                    <Badge variant={h.action === "created" ? "default" : "secondary"} className="rounded-sm capitalize">{h.action}</Badge>
                    <span className="text-xs text-muted-foreground font-mono">{new Date(h.at).toLocaleString()}</span>
                  </div>
                  {h.action === "created" ? (
                    <p className="text-sm text-muted-foreground">Asset registered.</p>
                  ) : (
                    <div className="space-y-1.5">
                      {h.changes.map((ch, i) => (
                        <div key={i} className="flex flex-wrap items-center gap-2 text-sm" data-testid={`asset-history-change-${ch.field}`}>
                          <span className="font-medium w-32 shrink-0 text-xs">{fieldLabel(ch.field)}</span>
                          <span className="font-mono text-xs px-2 py-0.5 bg-destructive/10 text-destructive rounded-sm line-through">{fmtVal(ch.old)}</span>
                          <span className="text-muted-foreground">→</span>
                          <span className="font-mono text-xs px-2 py-0.5 bg-[hsl(var(--success))]/10 text-[hsl(var(--success))] rounded-sm">{fmtVal(ch.new)}</span>
                        </div>
                      ))}
                    </div>
                  )}
                  <div className="text-[10px] text-muted-foreground mt-2">by {h.user}</div>
                </div>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
